import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    OnInit,
    signal,
    HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

import {
    DashboardResponse,
    DashboardService,
} from './services/dashboard.service';

import { CompanyService } from '@/app/shared/services/company.service';
import { AuthService } from '../authentication/services/auth.service';
import {
    Market,
    MarketService,
} from '@/app/shared/services/market.service';
import { InputFileDetails, RecentInputFileItem, RecentInputFilesResponse } from './models/input-file';


interface Company {
    id: number;
    name: string;
}

interface TransformationIssue {
    row?: number;
    code?: string;
    message?: string;
    input_column?: string;
    output_column?: string;
}

interface TransformationResultView {
    status: string | null;
    errors: TransformationIssue[];
    warnings: TransformationIssue[];
}

interface TransformationWarningGroup {
    inputColumn: string;
    warnings: TransformationIssue[];
}


@Component({
    selector: 'app-dashboard',
    standalone: true,
    imports: [
        CommonModule,
        RouterLink,
    ],
    templateUrl: './dashboard.component.html',
    styleUrl: './dashboard.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent implements OnInit {

    @HostListener('document:keydown.escape')
    onEscapeKey(): void {
        if (
            this.selectedFileDetails() !== null ||
            this.detailsLoading() ||
            this.detailsError() !== null
        ) {
            this.closeFileDetails();
        }
    }

    readonly Math = Math;

    private readonly dashboardService =
        inject(DashboardService);

    private readonly companyService =
        inject(CompanyService);

    private readonly marketService =
        inject(MarketService);

    private readonly authService =
        inject(AuthService);


    // -------------------------------------------------------------------------
    // State
    // -------------------------------------------------------------------------

    readonly companies =
        signal<Company[]>([]);

    readonly markets =
        signal<Market[]>([]);

    readonly selectedCompanyId =
        signal<number | null>(null);

    readonly selectedMarketId =
        signal<number | null>(null);

    readonly selectedDays =
        signal(7);

    readonly dashboard =
        signal<DashboardResponse | null>(null);

    readonly loading =
        signal(false);

    readonly error =
        signal<string | null>(null);

    // -------------------------------------------------------------------------
    // Recent input files
    // -------------------------------------------------------------------------

    readonly recentInputFiles =
        signal<RecentInputFileItem[]>([]);

    readonly recentFilesTotal =
        signal(0);

    readonly recentFilesLimit =
        signal(10);

    readonly recentFilesOffset =
        signal(0);

    readonly recentFilesLoading =
        signal(false);

    readonly recentFilesError =
        signal<string | null>(null);

    readonly showRawTransformationResult = signal(false);




    // -------------------------------------------------------------------------
    // Computed
    // -------------------------------------------------------------------------

    readonly selectedMarket = computed(() => {
        const marketId = this.selectedMarketId();

        return this.markets().find(
            market => market.id === marketId,
        );
    });

    readonly recentFilesCurrentPage = computed(
        () =>
            Math.floor(
                this.recentFilesOffset() /
                this.recentFilesLimit(),
            ) + 1,
    );

    readonly recentFilesTotalPages = computed(
        () =>
            Math.ceil(
                this.recentFilesTotal() /
                this.recentFilesLimit(),
            ),
    );

    readonly selectedFileDetails =
        signal<InputFileDetails | null>(null);

    readonly detailsLoading = signal(false);

    readonly detailsError =
        signal<string | null>(null);

    readonly selectedFileId = signal<number | null>(null);

    readonly transformationResult = computed<TransformationResultView | null>(() => {
        const result = this.selectedFileDetails()?.transformation?.result;

        if (
            result === null ||
            typeof result !== 'object' ||
            Array.isArray(result)
        ) {
            return null;
        }

        const data = result as Record<string, unknown>;

        const toIssues = (value: unknown): TransformationIssue[] => {
            if (!Array.isArray(value)) {
                return [];
            }

            return value.filter(
                (item): item is TransformationIssue =>
                    item !== null &&
                    typeof item === 'object' &&
                    !Array.isArray(item),
            );
        };

        return {
            status:
                typeof data['status'] === 'string'
                    ? data['status']
                    : null,
            errors: toIssues(data['errors']),
            warnings: toIssues(data['warnings']),
        };
    });

    readonly transformationWarningGroups = computed<TransformationWarningGroup[]>(() => {
        const warnings = this.transformationResult()?.warnings ?? [];
        const groups = new Map<string, TransformationIssue[]>();

        for (const warning of warnings) {
            const inputColumn = warning.input_column || 'Other';

            if (!groups.has(inputColumn)) {
                groups.set(inputColumn, []);
            }

            groups.get(inputColumn)!.push(warning);
        }

        return Array.from(groups.entries())
            .map(([inputColumn, items]) => ({
                inputColumn,
                warnings: items,
            }))
            .sort((a, b) =>
                a.inputColumn.localeCompare(b.inputColumn),
            );
    });



    // -------------------------------------------------------------------------
    // Lifecycle
    // -------------------------------------------------------------------------

    ngOnInit(): void {
        this.loadCompanies();
    }


    // -------------------------------------------------------------------------
    // Company
    // -------------------------------------------------------------------------

    private loadCompanies(): void {
        this.companyService
            .getCompanies()
            .subscribe({
                next: companies => {
                    this.companies.set(companies);

                    if (!companies.length) {
                        this.selectedCompanyId.set(null);
                        this.selectedMarketId.set(null);
                        this.dashboard.set(null);

                        this.error.set(
                            'No companies are available for the current user.',
                        );

                        return;
                    }

                    const preferredCompanyId =
                        this.authService
                            .userPreferences()
                            ?.company_id;

                    const company =
                        companies.find(
                            item =>
                                item.id ===
                                preferredCompanyId,
                        ) ?? companies[0];

                    this.selectedCompanyId.set(
                        company.id,
                    );

                    this.loadMarkets(
                        company.id,
                    );
                },

                error: () => {
                    this.companies.set([]);
                    this.markets.set([]);
                    this.selectedCompanyId.set(null);
                    this.selectedMarketId.set(null);
                    this.dashboard.set(null);

                    this.error.set(
                        'Failed to load companies.',
                    );
                },
            });
    }


    onCompanyChange(
        companyId: number,
    ): void {
        this.selectedCompanyId.set(
            companyId,
        );

        this.selectedMarketId.set(null);
        this.dashboard.set(null);
        this.error.set(null);

        this.recentInputFiles.set([]);
        this.recentFilesTotal.set(0);
        this.recentFilesOffset.set(0);
        this.recentFilesError.set(null);


        this.loadMarkets(companyId);
    }


    // -------------------------------------------------------------------------
    // Market
    // -------------------------------------------------------------------------

    private loadMarkets(
        companyId: number,
    ): void {
        this.marketService
            .getMarkets(companyId)
            .subscribe({
                next: markets => {
                    this.markets.set(markets);

                    if (!markets.length) {
                        this.selectedMarketId.set(null);
                        this.dashboard.set(null);

                        this.error.set(
                            'No markets are available for the selected company.',
                        );

                        return;
                    }

                    // const preferredMarketId =
                    //     this.authService
                    //         .userPreferences()
                    //         ?.market_id;

                    // const market =
                    //     markets.find(
                    //         item =>
                    //             item.id ===
                    //             preferredMarketId,
                    //     ) ?? markets[0];

                    // this.selectedMarketId.set(
                    //     market.id,
                    // );

                    // Default dashboard view = all markets
                    this.selectedMarketId.set(null);

                    this.loadDashboard();

                    this.recentFilesOffset.set(0);
                    this.loadRecentInputFiles();
                },

                error: () => {
                    this.markets.set([]);
                    this.selectedMarketId.set(null);
                    this.dashboard.set(null);

                    this.error.set(
                        'Failed to load markets.',
                    );
                },
            });
    }


    onMarketChange(
        value: string,
    ): void {

        const marketId =
            (value === 'all'
                ? null
                : Number(value))
            ;
        this.selectedMarketId.set(
            marketId,
        );

        this.loadDashboard();


        this.recentFilesOffset.set(0);
        this.loadRecentInputFiles();
    }


    // -------------------------------------------------------------------------
    // Period
    // -------------------------------------------------------------------------

    onPeriodChange(
        days: number,
    ): void {
        this.selectedDays.set(days);

        this.loadDashboard();
    }


    // -------------------------------------------------------------------------
    // Dashboard
    // -------------------------------------------------------------------------

    private loadDashboard(): void {
        const companyId =
            this.selectedCompanyId();

        const marketId =
            this.selectedMarketId();

        if (
            companyId == null
            // ||
            // marketId == null
        ) {
            return;
        }

        this.loading.set(true);
        this.error.set(null);

        this.dashboardService
            .getDashboard(
                companyId,
                marketId,
                this.selectedDays(),
            )
            .subscribe({
                next: response => {
                    this.dashboard.set(
                        response,
                    );

                    this.loading.set(false);
                },

                error: () => {
                    this.dashboard.set(null);

                    this.error.set(
                        'Failed to load dashboard.',
                    );

                    this.loading.set(false);
                },
            });
    }


    // -------------------------------------------------------------------------
    // API
    // -------------------------------------------------------------------------

    getApiSuccessRate(
        data: DashboardResponse,
    ): number {
        const total =
            data.api_delivery.sent +
            data.api_delivery.retrying +
            data.api_delivery.failed +
            data.api_delivery.not_queued;

        if (total === 0) {
            return 0;
        }

        return (
            data.api_delivery.sent /
            total
        ) * 100;
    }


    // -------------------------------------------------------------------------
    // Processing activity
    // -------------------------------------------------------------------------

    getActivityMax(): number {
        const activity =
            this.dashboard()
                ?.processing_activity ?? [];

        return Math.max(
            ...activity.map(
                item => item.received,
            ),
            1,
        );
    }


    getActivityHeight(
        value: number,
    ): number {
        return Math.max(
            (value / this.getActivityMax()) * 100,
            4,
        );
    }


    // -------------------------------------------------------------------------
    // Market status
    // -------------------------------------------------------------------------

    getMarketStatusClass(
        status: string,
    ): string {
        switch (status) {
            case 'PROCESSED':
                return 'success-status';

            case 'PROCESSING':
            case 'NO_DATA':
                return 'warning-status';

            case 'FAILED':
            default:
                return 'danger-status';
        }
    }


    getMarketStatusIcon(
        status: string,
    ): string {
        switch (status) {
            case 'PROCESSED':
                return 'pi pi-check';

            case 'PROCESSING':
            case 'NO_DATA':
                return 'pi pi-exclamation-triangle';

            case 'FAILED':
            default:
                return 'pi pi-times';
        }
    }

    // -------------------------------------------------------------------------
    // Recent input files
    // -------------------------------------------------------------------------

    loadRecentInputFiles(): void {
        const companyId = this.selectedCompanyId();
        const marketId = this.selectedMarketId();

        if (companyId == null
            //    || marketId == null
        ) {
            this.recentInputFiles.set([]);
            this.recentFilesTotal.set(0);
            this.recentFilesOffset.set(0);
            this.recentFilesError.set(null);
            return;
        }

        this.recentFilesLoading.set(true);
        this.recentFilesError.set(null);

        this.dashboardService
            .getRecentInputFiles(
                companyId,
                marketId,
                this.recentFilesLimit(),
                this.recentFilesOffset(),
            )
            .subscribe({
                next: (response: RecentInputFilesResponse) => {
                    this.recentInputFiles.set(response.items);
                    this.recentFilesTotal.set(response.total);
                    this.recentFilesLoading.set(false);
                },

                error: () => {
                    this.recentInputFiles.set([]);
                    this.recentFilesTotal.set(0);
                    this.recentFilesError.set(
                        'Failed to load input files.',
                    );
                    this.recentFilesLoading.set(false);
                },
            });
    }

    onRecentFilesPageChange(page: number): void {
        const totalPages = this.recentFilesTotalPages();

        if (page < 1 || page > totalPages) {
            return;
        }

        this.recentFilesOffset.set(
            (page - 1) * this.recentFilesLimit(),
        );

        this.loadRecentInputFiles();
    }

    openFileDetails(fileId: number): void {
        this.selectedFileId.set(fileId);
        this.detailsLoading.set(true);
        this.detailsError.set(null);
        this.selectedFileDetails.set(null);
        this.showRawTransformationResult.set(false);

        this.dashboardService
            .getInputFileDetails(fileId)
            .subscribe({
                next: (details) => {
                    this.selectedFileDetails.set(details);
                    this.detailsLoading.set(false);
                },
                error: () => {
                    this.detailsError.set(
                        'Failed to load file details.',
                    );
                    this.detailsLoading.set(false);
                },
            });
    }

    closeFileDetails(): void {
        this.selectedFileId.set(null);
        this.selectedFileDetails.set(null);
        this.detailsLoading.set(false);
        this.detailsError.set(null);
    }

    toggleRawTransformationResult(): void {
        this.showRawTransformationResult.update(value => !value);
    }

    downloadInputFile(fileId: number): void {
        this.dashboardService
            .downloadInputFile(fileId)
            .subscribe({
                next: blob => {
                    const url = window.URL.createObjectURL(blob);

                    const link =
                        document.createElement('a');

                    link.href = url;

                    const file =
                        this.recentInputFiles()
                            .find(item => item.id === fileId);

                    link.download =
                        file?.filename ??
                        `input-file-${fileId}`;

                    link.click();

                    link.remove();

                    window.URL.revokeObjectURL(url);
                },

                error: () => {
                    // po želji možemo ovdje koristiti
                    // posebnu downloadError signalu
                    console.error(
                        'Failed to download input file.',
                    );
                },
            });
    }

    downloadOutputArchive(fileId: number): void {
        this.dashboardService
            .downloadOutputArchive(fileId)
            .subscribe({
                next: blob => {
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');

                    const file = this.recentInputFiles().find(
                        item => item.id === fileId,
                    );

                    link.href = url;
                    link.download = `${file?.filename ?? `input-file-${fileId}`}.zip`;
                    link.click();

                    link.remove();
                    window.URL.revokeObjectURL(url);
                },
                error: () => {
                    console.error('Failed to download output archive.');
                },
            });
    }

    sendOutputArchiveToApi(fileId: number): void {
        this.dashboardService
            .sendOutputArchiveToApi(fileId)
            .subscribe({
                next: () => {
                    console.log(
                        'Output archive queued for API submission.',
                    );
                },
                error: error => {
                    console.error(
                        'Failed to queue output archive for API submission.',
                        error,
                    );
                },
            });
    }

    confirmSendOutputArchiveToApi(file: RecentInputFileItem): void {
        const confirmed = window.confirm(
            `Are you sure you want to send the output file for "${file.filename}" to the API again?`,
        );

        if (!confirmed) {
            return;
        }

        this.sendOutputArchiveToApi(file.id);
    }

    confirmReprocessFile(file: RecentInputFileItem): void {
    const confirmed = window.confirm(
        `Are you sure you want to reprocess "${file.filename}"? ` +
        `The existing output archive will be replaced and the new result will be sent to the external API.`
    );

    if (!confirmed) {
        return;
    }

    this.dashboardService.reprocessFile(file.id).subscribe({
        next: () => {
            // Osvježi dashboard kako bi prikazao novi status.
            this.loadRecentInputFiles();
        },
        error: (error) => {
            const message =
                error?.error?.detail ??
                'Failed to schedule file reprocessing.';

            window.alert(message);
        },
    });
}

}